/* eslint-disable react/sort-comp, brace-style, prefer-template */
import classnames from 'classnames';
import React from 'react';

/**
 * Classes that inherit ProgressBar should provide a reference to
 * meter (this.refs.meter).
 */
class ProgressBarBase extends React.Component {
  constructor(...args) {
    super(...args);

    this.state = {
      animateInterval: null,
      displayProgress: null,
      doAnimation: false,
      prevProgress: this.props.prevProgress,
    };

    // Needed to make sure jQuery progress bar stops animating.
    this._unmounted = false;
  }

  componentWillMount() {
    if (this.props.prevProgress !== null) {
      this.setState({
        doAnimation: true,
        displayProgress: this.state.prevProgress,
      });
    } else {
      this.setState({ displayProgress: this.props.progress });
    }
  }

  componentWillReceiveProps(nextProps) {
    // If the previous progress changes, modify the display progress
    // and start an animation.
    if (nextProps.prevProgress !== this.props.prevProgress) {
      this.setState({
        doAnimation: true,
        displayProgress: nextProps.prevProgress,
        prevProgress: nextProps.prevProgress,
      });

      this.stopAnimation();
    }

    // If the progress changes, but not the previous progress, then
    // continue animating from the original value.
    else if (nextProps.progress !== this.props.progress) {
      const prevProgress = this.state.prevProgress;

      this.setState({
        doAnimation: true,
        prevProgress: prevProgress !== null ? prevProgress : this.props.progress,
      });
    }
  }

  componentDidMount() {
    this._animate();
  }

  componentDidUpdate() {
    this._animate();
  }

  componentWillUnmount() {
    this._unmounted = true;
    this.stopAnimation();
  }

  _animate() {
    if (!this.state.doAnimation) {
      return;
    }

    this.stopAnimation();

    // Don't actually animate anything, just use a step function
    // to perform the animation by setting the state.
    const animateObj = $(this.refs.meter).animate({
      width: this.props.progress + '%',
    }, {
      duration: this.props.animationDurationMillis,

      // Make sure that new progress isn't less than or greater than
      // progress
      step: (newProgress) => {
        if (!this._unmounted) {
          const max = Math.max(this.props.progress, this.state.prevProgress);
          const min = Math.min(this.props.progress, this.state.prevProgress);
          const displayProgress = Math.max(Math.min(Math.round(newProgress), max), min);
          this.setState({ displayProgress });

          if (this.props.onAnimationIteration) {
            this.props.onAnimationIteration(displayProgress);
          }
        }
      },
    });

    this.setState({
      animateInterval: animateObj,
      doAnimation: false,
    });
  }

  stopAnimation() {
    if (this.state.animateInterval) {
      this.state.animateInterval.stop();
    }
  }
}

ProgressBarBase.propTypes = {
  animationDurationMillis: React.PropTypes.number,
  className: React.PropTypes.string,
  progress: React.PropTypes.number,
  prevProgress: React.PropTypes.number,
  onAnimationIteration: React.PropTypes.func,
};

ProgressBarBase.defaultProps = {
  animationDurationMillis: 1500,
  className: '',
  progress: 0,
  prevProgress: null,
};

/**
 * Default progress bar used for item results.
 */
export class ProgressBar extends ProgressBarBase {
  render() {
    const className = `progress-bar ${this.props.className}`;

    return (
      <div className="progress-bar-container">
        <div className={className} data-primary={this.state.displayProgress}>
          <span ref="meter" className="meter primary" style={{ width: this.state.displayProgress + '%' }}></span>
        </div>

        {this._renderDifference()}
        {this._renderProgressTag()}
      </div>
    );
  }

  // Renders progress made.
  _renderProgressTag() {
    if (!this.props.showProgressTag) {
      return '';
    }

    return (
      <span className="progress-tag">{this.state.displayProgress}%</span>
    );
  }

  // Renders the difference gained from the new progress.
  _renderDifference() {
    if (!this.props.showDifferenceTag || this.state.prevProgress === null) {
      return '';
    }

    const difference = this.state.displayProgress - this.state.prevProgress;
    const className = classnames('progress-difference', {
      'progress-loss': difference < 0,
      'progress-gain': difference > 0,
    });

    if (difference === 0) {
      return '';
    }

    // Negative sign will print, so don't need to check for that.
    return (
      <span className={className}>{difference > 0 ? '+' : '-'}{Math.abs(difference)}%</span>
    );
  }
}

ProgressBar.propTypes = Object.assign({
  showDifferenceTag: React.PropTypes.bool,
  showProgressTag: React.PropTypes.bool,
}, ProgressBarBase.propTypes);

ProgressBar.defaultProps = Object.assign({
  showProgressTag: false,
  showDifferenceTag: false,
}, ProgressBarBase.defaultProps);

export class ProgressBarSentenceTrainer extends ProgressBarBase {
  render() {
    const className = `progress-bar progress-bar-gradient ${this.props.className}`;

    // The background is the gradient and we want to treat it as though it
    // extends to the end. To eliminate the need to keep adding different
    // background sizes in CSS where ever this is used, we set the background-size
    // to be a percentage that would extend so it would be the full width of the
    // meter if it were at 100%.
    const style = {
      backgroundSize: (10000 / this.state.displayProgress) + '%',
      width: this.state.displayProgress + '%',
    };

    return (
      <div className="progress-bar-container">
        <div className={className} data-primary={this.state.displayProgress}>
          <span ref="meter" className="meter primary" style={style}></span>
        </div>
      </div>
    );
  }
}

// Send out the default ProgressBar
export default ProgressBar;
